import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import AdminCard from './AdminCard';
import AdminCardActions from './AdminCardActions';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAdmin } from './AdminHooks';
import { cn } from '../../lib/utils';
import { Utensils, Timer, RefreshCw, Pencil, CheckCircle, Send, Trash2, Plus, Euro, Minus, X, MessageSquare, CreditCard, BadgeCheck, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Textarea } from '../ui/textarea';
import { adminAPI } from './AdminUtils';

const formatDuration = (s) => {
  if (!s) return '';
  const ts = new Date(s).getTime();
  const diff = Math.max(0, Date.now() - ts);
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return `${mins}m ${secs}s`;
};

const timeBadgeClass = (s) => {
  if (!s) return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  const ts = new Date(s).getTime();
  const diff = Math.max(0, Date.now() - ts);
  const mins = diff / 60000;
  if (mins < 5) return 'bg-green-500/20 text-green-300 border-green-500/30';
  if (mins < 15) return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
  return 'bg-red-500/20 text-red-300 border-red-500/30';
};

const ModernOrdersTab = () => {
  const {
    events, tables, orders, codes,
    fetchTables, fetchOrders, fetchCodes, createTablesBulk, createTable, updateTable, deleteTable, updateOrder, finishOrder, deleteOrder, payOrder,
    ordersEventId, setOrdersEventId, ordersPolling, ordersPollingError,
  } = useAdmin();

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.matchMedia('(max-width: 767.98px)').matches; } catch { return false; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const mq = window.matchMedia('(max-width: 767.98px)');
      const handler = (e) => setIsMobile(e.matches);
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.onchange = handler;
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.onchange = null;
      };
    } catch {}
  }, []);
  const [viewMode, setViewMode] = useState('table');
  const [orderEditOpen, setOrderEditOpen] = useState(false);
  const [orderEditItems, setOrderEditItems] = useState([]);
  const [orderEditingId, setOrderEditingId] = useState('');
  const [orderEditComment, setOrderEditComment] = useState('');
  const [orderEditMeta, setOrderEditMeta] = useState({
    subtotal: 0,
    discountAmount: 0,
    discountPercent: null,
    discountFlat: null,
    total: 0,
    appliedCode: null,
  });
  const [orderEditPriceMap, setOrderEditPriceMap] = useState({});
  const [payConfirmOpen, setPayConfirmOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [payManyOpen, setPayManyOpen] = useState(false);
  const [payManyOrders, setPayManyOrders] = useState([]);
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [addOrderMenu, setAddOrderMenu] = useState({ dishes: [] });
  const [addOrderQuantities, setAddOrderQuantities] = useState({});
  const [addOrderComment, setAddOrderComment] = useState('');
  const [addOrderTableSlug, setAddOrderTableSlug] = useState('');
  const [addOrderSubmitting, setAddOrderSubmitting] = useState(false);
  const [addOrderSearch, setAddOrderSearch] = useState('');
  const [orderEditMenu, setOrderEditMenu] = useState({ dishes: [] });
  const [addOrderTab, setAddOrderTab] = useState('foods');
  const [editOrderTab, setEditOrderTab] = useState('foods');
  const [ordersTypeMap, setOrdersTypeMap] = useState({});
  const selectedTable = useMemo(() => {
    return (tables || []).find(t => t.slug === addOrderTableSlug) || null;
  }, [tables, addOrderTableSlug]);
  const selectedCode = useMemo(() => {
    const cid = selectedTable?.codeId;
    if (!cid) return null;
    return (codes || []).find(c => c.id === cid) || null;
  }, [selectedTable, codes]);

  useEffect(() => {
    if (ordersEventId) {
      fetchTables(ordersEventId);
      fetchOrders(ordersEventId);
    }
  }, [ordersEventId]);
  useEffect(() => { fetchCodes?.(); }, []);

  const onSelectEvent = (val) => {
    setOrdersEventId(val);
  };

  const activeOrders = (orders || []).filter(o => {
    const s = (o.status || 'open');
    return s === 'open';
  });
  const historyOrders = (orders || []).filter(o => {
    const s = (o.status || 'open');
    return s !== 'open' && s !== 'sent';
  });
  const sentOrders = (orders || []).filter(o => (o.status || 'open') === 'sent');

  const orderTotal = (o) => {
    if (typeof o.total === 'number') return o.total;
    return (o.items || []).reduce((sum, it) => {
      const qty = Number(it.quantity || 0);
      const price = Number(it.price || 0);
      return sum + qty * price;
    }, 0);
  };
  const orderBreakdown = (o) => {
    const subtotalRaw = typeof o.subtotal === 'number'
      ? o.subtotal
      : (o.items || []).reduce((s, it) => s + Number(it.price || 0) * Number(it.quantity || 0), 0);
    const subtotal = Math.round(subtotalRaw * 100) / 100;
    const fromTotal = orderTotal(o);
    const discountField = Number(o.discountAmount || 0);
    const delta = Math.max(0, subtotal - Math.round(fromTotal * 100) / 100);
    let discount = Math.round(Math.max(discountField, delta) * 100) / 100;
    try {
      const tinfo = (tables || []).find(t => t.id === o.tableId);
      const codeObj = tinfo?.codeId ? (codes || []).find(c => c.id === tinfo.codeId) : null;
      if (codeObj) {
        const perc = Number(codeObj.percent || 0);
        const flat = Number(codeObj.flat || 0);
        const byPerc = perc > 0 ? Math.round((subtotal * (perc / 100)) * 100) / 100 : 0;
        const byFlat = flat > 0 ? Math.round(flat * 100) / 100 : 0;
        discount = Math.min(subtotal, Math.max(byPerc, byFlat));
      }
    } catch {}
    const total = Math.max(0, Math.round((subtotal - discount) * 100) / 100);
    return { subtotal, discount, total };
  };
  useEffect(() => {
    if (!orderEditItems || !orderEditItems.length) return;
    const pm = orderEditPriceMap || {};
    let subtotal = 0;
    for (const it of orderEditItems) {
      const q = Number(it.quantity || 0);
      const p = Number(pm[it.name] || 0);
      subtotal += q * p;
    }
    let disc = 0;
    const perc = orderEditMeta.discountPercent != null ? Number(orderEditMeta.discountPercent) : null;
    const flat = orderEditMeta.discountFlat != null ? Number(orderEditMeta.discountFlat) : null;
    if (perc && perc > 0) disc += Math.round((subtotal * (perc / 100)) * 100) / 100;
    if (flat && flat > 0) disc += Math.round(flat * 100) / 100;
    const total = Math.max(0, Math.round((subtotal - disc) * 100) / 100);
    setOrderEditMeta(prev => ({
      ...prev,
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: Math.round(disc * 100) / 100,
      total,
    }));
  }, [orderEditItems, orderEditPriceMap]);
  const formatEUR = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const renderTableLabel = (tableInfo) => (
    <div className="flex gap-2 items-center">
      <span className="font-medium">{tableInfo?.name || '—'}</span>
      {tableInfo?.number ? (
        <Badge className="bg-slate-700 text-slate-200 border-slate-600">#{tableInfo.number}</Badge>
      ) : null}
    </div>
  );
  const groupByTable = (list) => {
    const map = {};
    (list || []).forEach(o => {
      const tid = o.tableId;
      if (!map[tid]) map[tid] = [];
      map[tid].push(o);
    });
    return map;
  };
  const sentByTable = groupByTable(sentOrders);
  const paidOrders = (orders || []).filter(o => (o.status || '') === 'paid');
  const [expandedToPay, setExpandedToPay] = useState({});
  const formatDateTime = (s) => s ? new Date(s).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
  const groupByType = (dishes) => {
    const groups = { starter: [], main: [], dessert: [] };
    (dishes || []).forEach(d => {
      const t = (d.type || 'main').toLowerCase();
      if (!groups[t]) groups[t] = [];
      groups[t].push(d);
    });
    return groups;
  };
  const addGroups = useMemo(() => groupByType(addOrderMenu?.dishes || []), [addOrderMenu]);
  const addDrinks = useMemo(() => (addOrderMenu?.dishes || []).filter(d => (d.type || '').toLowerCase() === 'drink'), [addOrderMenu]);
  const editGroups = useMemo(() => groupByType(orderEditMenu?.dishes || []), [orderEditMenu]);
  const viewGroups = useMemo(() => {
    const q = (addOrderSearch || '').trim().toLowerCase();
    if (!q) return addGroups;
    const filterDish = (d) => {
      const n = (d.name || '').toLowerCase();
      const desc = (d.description || '').toLowerCase();
      return n.includes(q) || desc.includes(q);
    };
    return {
      starter: (addGroups.starter || []).filter(filterDish),
      main: (addGroups.main || []).filter(filterDish),
      dessert: (addGroups.dessert || []).filter(filterDish),
    };
  }, [addGroups, addOrderSearch]);
  const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlightText = (text) => {
    const q = (addOrderSearch || '').trim();
    if (!q) return text;
    const re = new RegExp(escapeRegExp(q), 'ig');
    const str = String(text || '');
    let last = 0;
    const nodes = [];
    let m;
    while ((m = re.exec(str))) {
      if (m.index > last) nodes.push(str.slice(last, m.index));
      nodes.push({ h: str.slice(m.index, m.index + m[0].length) });
      last = m.index + m[0].length;
    }
    if (last < str.length) nodes.push(str.slice(last));
    return nodes.map((seg, i) =>
      typeof seg === 'string'
        ? seg
        : <span key={i} className="px-0.5 rounded bg-yellow-500/30 text-yellow-200">{seg.h}</span>
    );
  };
  useEffect(() => {
    const loadMenu = async () => {
      if (!ordersEventId || !addOrderOpen) return;
      try {
        const res = await adminAPI.get(`/menus/${ordersEventId}`);
        setAddOrderMenu(res.data.menu || { dishes: [] });
      } catch {
        setAddOrderMenu({ dishes: [] });
      }
    };
    loadMenu();
  }, [ordersEventId, addOrderOpen]);
  useEffect(() => {
    const loadTypes = async () => {
      if (!ordersEventId) return;
      try {
        const res = await adminAPI.get(`/menus/${ordersEventId}`);
        const m = {};
        ((res.data.menu?.dishes) || []).forEach(d => { if (d?.name) m[d.name] = (d.type || '').toLowerCase(); });
        setOrdersTypeMap(m);
      } catch {
        setOrdersTypeMap({});
      }
    };
    loadTypes();
  }, [ordersEventId]);
  useEffect(() => {
    const loadEditMenu = async () => {
      if (!ordersEventId || !orderEditOpen) return;
      try {
        const res = await adminAPI.get(`/menus/${ordersEventId}`);
        setOrderEditMenu(res.data.menu || { dishes: [] });
      } catch {
        setOrderEditMenu({ dishes: [] });
      }
    };
    loadEditMenu();
  }, [ordersEventId, orderEditOpen]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Commandes</h2>
          <p className="text-slate-400">
            {tables.length} table(s) • {orders.length} commande(s)
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={ordersEventId} onValueChange={onSelectEvent}>
            <SelectTrigger className="w-full sm:w-56 md:w-48 lg:w-[220px] bg-slate-900 border-slate-700 text-white">
              <SelectValue placeholder="Sélectionner l'événement" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {(events || []).map(e => (
                <SelectItem key={e.id} value={e.id} className="text-white">
                  Vol.{e.volume} • {e.theme}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge className={cn(
            "inline-flex gap-1 items-center",
            ordersPollingError
              ? "text-amber-200 bg-amber-500/20 border-amber-500/30"
              : ordersPolling
                ? "text-blue-300 bg-blue-500/20 border-blue-500/30"
                : "bg-slate-700 text-slate-300 border-slate-600"
          )}>
            <RefreshCw size={12} className={ordersPolling ? 'animate-spin' : ''} />
            {ordersPollingError ? 'Incident live' : ordersPolling ? 'Actualisation' : 'Pause'}
          </Badge>
          <div className="hidden ml-2 rounded-lg border sm:flex bg-slate-900 border-slate-700">
            <Button
              size="sm"
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              onClick={() => setViewMode('table')}
              className={cn(
                "rounded-r-none",
                viewMode === 'table' && "bg-blue-600 text-white"
              )}
            >
              Tableau
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              onClick={() => setViewMode('cards')}
              className={cn(
                "rounded-l-none",
                viewMode === 'cards' && "bg-blue-600 text-white"
              )}
            >
              Cartes
            </Button>
          </div>
        </div>
      </div>
      {ordersPollingError && ordersEventId ? (
        <div className="flex gap-2 items-start px-3 py-2 rounded-lg border bg-amber-500/10 border-amber-500/30 text-amber-100">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-300" />
          <p className="text-sm">
            La mise a jour en direct rencontre un probleme. Les dernieres commandes visibles restent affichees. Si cela continue, utilisez Actualiser.
          </p>
        </div>
      ) : null}
      <div className="flex justify-end">
        <Button
          onClick={() => setAddOrderOpen(true)}
          disabled={!ordersEventId || (tables || []).length === 0}
          className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          title="Ajouter une commande"
          aria-label="Ajouter une commande"
        >
          <Plus size={14} className="mr-2" />
          Ajouter une commande
        </Button>
      </div>
      <div className="space-y-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Commandes</CardTitle>
          </CardHeader>
          <CardContent>
            {viewMode === 'table' ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-slate-800/30">
                      <TableHead className="text-slate-300">Table</TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">Articles</TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">
                        <MessageSquare size={14} className="mx-auto" title="Commentaire" />
                      </TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">
                        <Timer size={14} className="mx-auto" title="Depuis" />
                      </TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">
                        <CreditCard size={14} className="mx-auto" title="Paiement" />
                      </TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">Prix</TableHead>
                      <TableHead className="text-right border-l text-slate-300 border-slate-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="py-8 text-center text-slate-400">
                          Aucune commande
                        </TableCell>
                      </TableRow>
                    ) : (
                      activeOrders.map(o => (
                        <TableRow key={o.id} className="transition-colors group border-slate-700 hover:bg-slate-800/30">
                          <TableCell className="text-white">
                            {renderTableLabel(tables.find(t => t.id === o.tableId))}
                          </TableCell>
                          <TableCell className="text-center">
                            <Popover>
                              <PopoverTrigger asChild>
                                <div className="w-full max-w-[22rem] mx-auto text-left cursor-pointer" title="Voir tous les articles">
                                  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
                                    {(o.items || []).map((it, idx) => (
                                      <React.Fragment key={idx}>
                                        <div className={cn("truncate", (ordersTypeMap[it.name] === 'drink') ? "text-fuchsia-300" : "text-slate-200")}>{it.name}</div>
                                        <div className="text-right">
                                          <Badge className={cn((ordersTypeMap[it.name] === 'drink') ? "text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30" : "bg-slate-700 text-slate-200 border-slate-600")}>
                                            {it.quantity}
                                          </Badge>
                                        </div>
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                              </PopoverTrigger>
                              <PopoverContent className="w-96 max-w-[90vw] max-h-[60vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-200">
                                <div className="flex flex-col gap-2">
                                  {(o.items || []).map((it, idx) => {
                                    const isDrink = (ordersTypeMap[it.name] === 'drink');
                                    return (
                                      <div key={idx} className="flex justify-between items-center">
                                        <div className={cn("truncate", isDrink ? "text-fuchsia-300" : "text-slate-200")}>{it.name}</div>
                                        <Badge className={cn(isDrink ? "text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30" : "bg-slate-700 text-slate-200 border-slate-600")}>
                                          {it.quantity}
                                        </Badge>
                                      </div>
                                    );
                                  })}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </TableCell>
                          {/* Type column removed for responsiveness */}
                          <TableCell className="text-center">
                            {o.comment ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="w-8 h-8 text-slate-300 hover:text-white hover:bg-slate-700"
                                    title="Voir le commentaire"
                                  >
                                    <MessageSquare size={16} />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-96 max-w-[90vw] bg-slate-900 border-slate-700 text-slate-200">
                                  <div className="whitespace-pre-wrap">{o.comment}</div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <span className="text-slate-500">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {((o.status || 'open') === 'open') ? (
                              <Badge className={cn("inline-flex items-center gap-1 whitespace-nowrap", timeBadgeClass(o.createdAt))}>
                                {formatDuration(o.createdAt)}
                              </Badge>
                            ) : (
                              <Badge className="inline-flex gap-1 items-center whitespace-nowrap bg-slate-700 text-slate-300 border-slate-600">
                                Pause
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center group-hover:bg-slate-800/30">
                            <div className="inline-flex justify-center items-center">
                              {o.paidAt ? (
                                <CheckCircle size={18} className="text-emerald-400" title="Payée" />
                              ) : (
                                <X size={18} className="text-red-400" title="Non payée" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center group-hover:bg-slate-800/30">
                            {(() => {
                              const bd = orderBreakdown(o);
                              const tinfo = (tables || []).find(t => t.id === o.tableId);
                              const codeObj = tinfo?.codeId ? (codes || []).find(c => c.id === tinfo.codeId) : null;
                              const codeLabel = codeObj?.code || null;
                              return (
                                <div className="inline-flex flex-col gap-1 items-center">
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Badge className="cursor-pointer bg-slate-700 text-slate-200 border-slate-600" title="Voir le détail">
                                        {formatEUR(bd.total)}
                                      </Badge>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-80 bg-slate-900 border-slate-700 text-slate-200">
                                      <div className="flex flex-col gap-2">
                                        <div className="flex justify-between items-center">
                                          <span className="text-slate-400">Sous-total</span>
                                          <span className="font-medium">{formatEUR(bd.subtotal)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                          <span className="text-slate-400">Remise</span>
                                          <span className="font-medium text-orange-300">-{formatEUR(bd.discount)}</span>
                                        </div>
                                        <div className="h-px bg-slate-700" />
                                        <div className="flex justify-between items-center">
                                          <span className="text-slate-200">Total</span>
                                          <span className="font-bold text-emerald-300">{formatEUR(bd.total)}</span>
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-right group-hover:bg-slate-800/30">
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => {
                                  setOrderEditingId(o.id);
                                  setOrderEditItems((o.items || []).map(it => ({ name: it.name, quantity: it.quantity })));
                                  setOrderEditComment(o.comment || '');
                                  setOrderEditMeta({
                                    subtotal: typeof o.subtotal === 'number' ? o.subtotal : orderTotal(o),
                                    discountAmount: typeof o.discountAmount === 'number' ? o.discountAmount : 0,
                                    discountPercent: o.discountPercent != null ? o.discountPercent : null,
                                    discountFlat: o.discountFlat != null ? o.discountFlat : null,
                                    total: typeof o.total === 'number' ? o.total : orderTotal(o),
                                    appliedCode: o.appliedCode || null,
                                  });
                                  const map = {};
                                  (o.items || []).forEach(it => { map[it.name] = Number(it.price || 0); });
                                  setOrderEditPriceMap(map);
                                  setOrderEditOpen(true);
                                }}
                                className="h-12 w-12 text-slate-300 hover:text-white hover:bg-slate-700 [&_svg]:size-5"
                                aria-label="Modifier commande"
                                title="Modifier"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => {
                                  setPayTarget(o);
                                  setPayConfirmOpen(true);
                                }}
                                className="h-12 w-12 text-slate-300 hover:text-green-400 hover:bg-green-500/10 [&_svg]:size-5"
                                aria-label="Payer"
                                title="Paiement"
                              >
                                <Euro size={14} />
                              </Button>
                              {((o.status || 'open') === 'open') ? (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => {
                                    setSendTarget(o);
                                    setSendConfirmOpen(true);
                                  }}
                                  className="h-12 w-12 text-slate-300 hover:text-orange-300 hover:bg-orange-500/10 [&_svg]:size-5"
                                  aria-label="Envoyer"
                                  title="Marquer comme envoyée"
                                >
                                  <Send size={14} />
                                </Button>
                              ) : (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => {
                                    if (!ordersEventId) return;
                                    finishOrder(ordersEventId, o.id);
                                  }}
                                  className="h-12 w-12 text-slate-300 hover:text-green-400 hover:bg-green-500/10 [&_svg]:size-5"
                                  aria-label="Terminer"
                                  title="Terminer (Payée)"
                                >
                                  <CheckCircle size={14} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {activeOrders.length === 0 ? (
                  <div className="p-6 text-center text-slate-400">Aucune commande</div>
                ) : (
                  activeOrders.map((o) => {
                    const tinfo = tables.find(t => t.id === o.tableId);
                    const codeObj = tinfo?.codeId ? (codes || []).find(c => c.id === tinfo.codeId) : null;
                    const code = codeObj?.code || null;
                    const isSent = (o.status || 'open') === 'sent';
                    const bd = orderBreakdown(o);
                    return (
                      <AdminCard key={o.id}>
                        <CardHeader className="pb-3">
                          <div className="flex gap-2 items-center">
                            <CardTitle className="text-lg text-white truncate">{tinfo?.name || '—'}</CardTitle>
                          </div>
                          <div className="flex gap-2 items-center mt-2">
                            {((o.status || 'open') === 'open') ? (
                              <Badge className={cn("inline-flex items-center gap-1", timeBadgeClass(o.createdAt))}>
                                <Timer size={12} />
                                {formatDuration(o.createdAt)}
                              </Badge>
                            ) : (
                              <Badge className="inline-flex gap-1 items-center bg-slate-700 text-slate-300 border-slate-600">
                                <Timer size={12} />
                                Pause
                              </Badge>
                            )}
                            <Badge className={cn(
                              "hidden gap-1 items-center sm:inline-flex",
                              isSent ? "text-orange-300 bg-orange-500/20 border-orange-500/30" : "bg-slate-700 text-slate-300 border-slate-600"
                            )}>
                              {isSent ? 'Envoyée' : 'Préparation'}
                            </Badge>
                            <Badge className={cn(
                              "hidden sm:inline-flex gap-1 items-center",
                              o.paidAt ? "text-emerald-300 bg-emerald-500/20 border-emerald-500/30" : "bg-slate-700 text-slate-300 border-slate-600"
                            )}>
                              {o.paidAt ? 'Payée' : 'Non payée'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                              <div className="flex gap-1 items-center text-xs tracking-wide uppercase text-slate-400">
                                <span className="hidden sm:inline">Articles</span>
                                <Utensils size={12} className="sm:hidden" />
                                <Badge className="ml-1 px-1 py-0.5 text-[10px] bg-slate-800 text-slate-300 border-slate-700">
                                  {(o.items || []).length}
                                </Badge>
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <div className="flex flex-col gap-1 mt-1 cursor-pointer" title="Voir tous les articles">
                                    {(() => {
                                      const items = o.items || [];
                                      const preview = items.slice(0, 4);
                                      const more = items.length - preview.length;
                                      return (
                                        <>
                                          {preview.map((it, idx) => {
                                            const isDrink = (ordersTypeMap[it.name] || '') === 'drink';
                                            const cls = isDrink
                                              ? "px-2 py-0.5 text-xs text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30"
                                              : "px-2 py-0.5 text-xs bg-slate-700 text-slate-200 border-slate-600";
                                            return (
                                              <Badge key={idx} className={cls}>
                                                {it.name} × {it.quantity}
                                              </Badge>
                                            );
                                          })}
                                          {more > 0 && (
                                            <Badge className="px-2 py-0.5 text-xs bg-slate-700 text-slate-400 border-slate-600">
                                              +{more} autres
                                            </Badge>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </PopoverTrigger>
                                <PopoverContent className="w-96 max-w-[90vw] max-h-[60vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-200">
                                  <div className="flex flex-col gap-2">
                                    {(o.items || []).map((it, idx) => {
                                      const isDrink = (ordersTypeMap[it.name] || '') === 'drink';
                                      return (
                                        <div key={idx} className="flex justify-between items-center">
                                          <div className={cn("truncate", isDrink ? "text-fuchsia-300" : "text-slate-200")}>{it.name}</div>
                                          <Badge className={cn(isDrink ? "text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30" : "bg-slate-700 text-slate-200 border-slate-600")}>
                                            {it.quantity}
                                          </Badge>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                            <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                              <div className="flex gap-1 items-center text-xs tracking-wide uppercase text-slate-400">
                                <span className="hidden sm:inline">Commentaire</span>
                                <MessageSquare size={12} className="sm:hidden" />
                              </div>
                              {o.comment ? (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <div className="mt-1 text-sm truncate cursor-pointer text-slate-300" title="Voir le commentaire">
                                      {o.comment}
                                    </div>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-96 max-w-[90vw] bg-slate-900 border-slate-700 text-slate-200">
                                    <div className="whitespace-pre-wrap">{o.comment}</div>
                                  </PopoverContent>
                                </Popover>
                              ) : (
                                <div className="mt-1 text-sm text-slate-300">—</div>
                              )}
                            </div>
                            <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                              <div className="text-xs tracking-wide uppercase text-slate-400">Prix</div>
                              <div className="flex flex-col gap-1 items-start mt-1">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Badge className="cursor-pointer bg-slate-700 text-slate-200 border-slate-600" title="Voir le détail">
                                      {formatEUR(bd.total)}
                                    </Badge>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-80 bg-slate-900 border-slate-700 text-slate-200">
                                    <div className="flex flex-col gap-2">
                                      <div className="flex justify-between items-center">
                                        <span className="text-slate-400">Sous-total</span>
                                        <span className="font-medium">{formatEUR(bd.subtotal)}</span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-slate-400">Remise</span>
                                        <span className="font-medium text-orange-300">-{formatEUR(bd.discount)}</span>
                                      </div>
                                      <div className="h-px bg-slate-700" />
                                      <div className="flex justify-between items-center">
                                        <span className="text-slate-200">Total</span>
                                        <span className="font-bold text-emerald-300">{formatEUR(bd.total)}</span>
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                                {bd.discount > 0 && code && (
                                  <Badge className="bg-slate-700 text-slate-300 border-slate-600">
                                    Code: {code}
                                    {typeof codeObj?.percent === 'number' ? ` • ${codeObj.percent}%` : ''}
                                    {typeof codeObj?.flat === 'number' ? ` • ${formatEUR(codeObj.flat)}` : ''}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                        <AdminCardActions>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setOrderEditingId(o.id);
                              setOrderEditItems((o.items || []).map(it => ({ name: it.name, quantity: it.quantity })));
                              setOrderEditComment(o.comment || '');
                              setOrderEditMeta({
                                subtotal: typeof o.subtotal === 'number' ? o.subtotal : orderTotal(o),
                                discountAmount: typeof o.discountAmount === 'number' ? o.discountAmount : 0,
                                discountPercent: o.discountPercent != null ? o.discountPercent : null,
                                discountFlat: o.discountFlat != null ? o.discountFlat : null,
                                total: typeof o.total === 'number' ? o.total : orderTotal(o),
                                appliedCode: o.appliedCode || null,
                              });
                              const map = {};
                              (o.items || []).forEach(it => { map[it.name] = Number(it.price || 0); });
                              setOrderEditPriceMap(map);
                              setOrderEditOpen(true);
                            }}
                            className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300 hover:text-white hover:bg-slate-700 [&_svg]:size-5"
                            title="Modifier"
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => { setPayTarget(o); setPayConfirmOpen(true); }}
                            className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300 hover:text-green-400 hover:bg-green-500/10 [&_svg]:size-5"
                            title="Paiement"
                          >
                            <Euro size={14} />
                          </Button>
                          {((o.status || 'open') === 'open') ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => { setSendTarget(o); setSendConfirmOpen(true); }}
                              className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300 hover:text-orange-300 hover:bg-orange-500/10 [&_svg]:size-5"
                              title="Envoyer"
                            >
                              <Send size={14} />
                            </Button>
                          ) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => { if (!ordersEventId) return; finishOrder(ordersEventId, o.id); }}
                              className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300 hover:text-green-400 hover:bg-green-500/10 [&_svg]:size-5"
                              title="Terminer"
                            >
                              <CheckCircle size={14} />
                            </Button>
                          )}
                        </AdminCardActions>
                      </AdminCard>
                    );
                  })
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Card className="mt-4 bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">À payer</CardTitle>
        </CardHeader>
        <CardContent>
          {viewMode === 'table' ? (
            <div className="overflow-x-auto">
              <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-slate-800/30">
                      <TableHead className="text-center text-slate-300">
                        <Utensils size={14} className="mx-auto" title="Table" />
                      </TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">
                        <Euro size={14} className="mx-auto" title="Total" />
                      </TableHead>
                      <TableHead className="text-right border-l text-slate-300 border-slate-700">
                        <CheckCircle size={14} className="inline" title="Actions" />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {Object.keys(sentByTable).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="py-8 text-center text-slate-400">
                        Aucune commande à payer
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(sentByTable).map(([tid, list]) => {
                      const tinfo = tables.find(t => t.id === tid);
                      const subtotals = list.map(o => orderBreakdown(o).subtotal);
                      const totals = list.map(o => orderBreakdown(o).total);
                      const discounts = list.map(o => orderBreakdown(o).discount);
                      const subtotalSum = subtotals.reduce((a, b) => a + b, 0);
                      const total = totals.reduce((a, b) => a + b, 0);
                      const discountSum = discounts.reduce((a, b) => a + b, 0);
                      const exp = !!expandedToPay[tid];
                      return (
                        <React.Fragment key={tid}>
                          <TableRow className="transition-colors border-slate-700 hover:bg-slate-800/30">
                            <TableCell className="text-white">
                              {renderTableLabel(tinfo)}
                            </TableCell>
                            <TableCell className="text-center">
                              {discountSum > 0 ? (
                                <div className="inline-flex flex-wrap gap-1 justify-center">
                                  <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                    {formatEUR(subtotalSum)}
                                  </Badge>
                                  <Badge className="text-orange-300 bg-orange-500/20 border-orange-500/30">
                                    -{formatEUR(discountSum)}
                                  </Badge>
                                  <Badge className="text-emerald-300 bg-emerald-500/20 border-emerald-500/30">
                                    {formatEUR(total)}
                                  </Badge>
                                </div>
                              ) : (
                                <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                  {formatEUR(total)}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="lg"
                                  variant="ghost"
                                  onClick={() => setExpandedToPay(prev => ({ ...prev, [tid]: !exp }))}
                                  className="px-5 h-12 text-slate-300 hover:text-white hover:bg-slate-700"
                                  aria-label="Détails"
                                  title="Voir les détails"
                                >
                                  {exp ? 'Masquer' : 'Voir'}
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => { setPayManyOrders(list); setPayManyOpen(true); }}
                                  className="h-12 w-12 text-slate-300 hover:text-green-400 hover:bg-green-500/10 [&_svg]:size-5"
                                  aria-label="Payer"
                                  title="Marquer comme payées"
                                >
                                  <CheckCircle size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          {exp && (
                            <TableRow>
                              <TableCell colSpan={3}>
                                <div className="p-3 rounded-lg border border-slate-700 bg-slate-900/40">
                                  <div className="flex flex-col gap-2">
                                    {list.map(o => {
                                      const bd = orderBreakdown(o);
                                      const st = bd.subtotal;
                                      const disc = bd.discount;
                                      const tot = bd.total;
                                      return (
                                        <div key={o.id} className="flex justify-between items-center">
                                          <div className="flex flex-wrap gap-1">
                                            {(o.items || []).map((it, idx) => (
                                              <Badge key={idx} className="bg-slate-700 text-slate-200 border-slate-600">
                                                {it.name} × {it.quantity}
                                              </Badge>
                                            ))}
                                          </div>
                                          <div className="flex flex-wrap gap-1 items-center">
                                            {disc > 0 ? (
                                              <>
                                                <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                                  {formatEUR(st)}
                                                </Badge>
                                                <Badge className="text-orange-300 bg-orange-500/20 border-orange-500/30">
                                                  -{formatEUR(disc)}
                                                </Badge>
                                                <Badge className="text-emerald-300 bg-emerald-500/20 border-emerald-500/30">
                                                  {formatEUR(tot)}
                                                </Badge>
                                              </>
                                            ) : (
                                              <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                                {formatEUR(tot)}
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Object.keys(sentByTable).length === 0 ? (
                <div className="p-6 text-center text-slate-400">Aucune commande à payer</div>
              ) : (
                Object.entries(sentByTable).map(([tid, list]) => {
                  const tinfo = tables.find(t => t.id === tid);
                  const subtotals = list.map(o => orderBreakdown(o).subtotal);
                  const totals = list.map(o => orderBreakdown(o).total);
                  const discounts = list.map(o => orderBreakdown(o).discount);
                  const subtotalSum = subtotals.reduce((a, b) => a + b, 0);
                  const total = totals.reduce((a, b) => a + b, 0);
                  const discountSum = discounts.reduce((a, b) => a + b, 0);
                  const exp = !!expandedToPay[tid];
                  return (
                    <AdminCard key={tid}>
                      <CardHeader className="pb-3">
                        <div className="flex gap-2 items-center">
                          <Utensils size={16} className="text-blue-400" />
                          <CardTitle className="text-lg text-white truncate">{tinfo?.name || '—'}</CardTitle>
                          <Badge className="bg-slate-700 text-slate-200 border-slate-600">#{tinfo?.number || '—'}</Badge>
                          {discountSum > 0 ? (
                            <div className="inline-flex flex-wrap gap-1 items-center ml-auto">
                              <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                {formatEUR(subtotalSum)}
                              </Badge>
                              <Badge className="text-orange-300 bg-orange-500/20 border-orange-500/30">
                                -{formatEUR(discountSum)}
                              </Badge>
                              <Badge className="text-emerald-300 bg-emerald-500/20 border-emerald-500/30">
                                {formatEUR(total)}
                              </Badge>
                            </div>
                          ) : (
                            <Badge className="ml-auto bg-slate-700 text-slate-200 border-slate-600">{formatEUR(total)}</Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {exp && (
                          <div className="p-3 mt-3 rounded-lg border border-slate-700 bg-slate-900/40">
                            <div className="flex flex-col gap-2">
                              {list.map(o => {
                                const st = typeof o.subtotal === 'number'
                                  ? o.subtotal
                                  : (o.items || []).reduce((s, it) => s + Number(it.price || 0) * Number(it.quantity || 0), 0);
                                const disc = Number(o.discountAmount || 0);
                                const tot = orderTotal(o);
                                return (
                                  <div key={o.id} className="flex justify-between items-center">
                                    <div className="flex flex-wrap gap-1">
                                      {(o.items || []).map((it, idx) => {
                                        const isDrink = (ordersTypeMap[it.name] || '') === 'drink';
                                        return (
                                          <Badge key={idx} className={cn(isDrink ? "text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30" : "bg-slate-700 text-slate-200 border-slate-600")}>
                                            {it.name} × {it.quantity}
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                    <div className="flex flex-wrap gap-1 items-center">
                                      {disc > 0 ? (
                                        <>
                                          <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                            {formatEUR(st)}
                                          </Badge>
                                          <Badge className="text-orange-300 bg-orange-500/20 border-orange-500/30">
                                            -{formatEUR(disc)}
                                          </Badge>
                                          <Badge className="text-emerald-300 bg-emerald-500/20 border-emerald-500/30">
                                            {formatEUR(tot)}
                                          </Badge>
                                        </>
                                      ) : (
                                        <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                          {formatEUR(tot)}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </CardContent>
                      <AdminCardActions>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedToPay(prev => ({ ...prev, [tid]: !exp }))}
                          className="text-slate-300 hover:text-white hover:bg-slate-700"
                          title={exp ? 'Masquer' : 'Voir'}
                        >
                          {exp ? 'Masquer' : 'Voir'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setPayManyOrders(list); setPayManyOpen(true); }}
                          className="text-slate-300 hover:text-green-400 hover:bg-green-500/10"
                          title="Marquer comme payées"
                        >
                          <CheckCircle size={14} />
                        </Button>
                      </AdminCardActions>
                    </AdminCard>
                  );
                })
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6 bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Historique des commandes</CardTitle>
        </CardHeader>
        <CardContent>
          {viewMode === 'table' ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-slate-800/30">
                    <TableHead className="text-slate-300">Table</TableHead>
                      <TableHead className="text-center border-l text-slate-300 border-slate-700">Articles</TableHead>
                    <TableHead className="text-center border-l text-slate-300 border-slate-700">Payée à</TableHead>
                    <TableHead className="text-center border-l text-slate-300 border-slate-700">Total</TableHead>
                    <TableHead className="text-right border-l text-slate-300 border-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paidOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-slate-400">
                        Aucun historique
                      </TableCell>
                    </TableRow>
                  ) : (
                    paidOrders.map(o => {
                      const tinfo = tables.find(t => t.id === o.tableId);
                      return (
                        <TableRow key={o.id} className="transition-colors border-slate-700 hover:bg-slate-800/30">
                          <TableCell className="text-white">
                            {renderTableLabel(tinfo)}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {(o.items || []).map((it, idx) => {
                                const isDrink = (ordersTypeMap[it.name] || '') === 'drink';
                                return (
                                  <Badge key={idx} className={cn(isDrink ? "text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30" : "bg-slate-700 text-slate-200 border-slate-600")}>
                                    {it.name} × {it.quantity}
                                  </Badge>
                                );
                              })}
                            </div>
                          </TableCell>
                          {/* Type column removed for responsiveness */}
                          <TableCell className="text-center">
                            <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                              {formatDateTime(o.paidAt)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {(() => {
                              const bd = orderBreakdown(o);
                              const tinfo = (tables || []).find(t => t.id === o.tableId);
                              const codeObj = tinfo?.codeId ? (codes || []).find(c => c.id === tinfo.codeId) : null;
                              const codeLabel = codeObj?.code || null;
                              return (
                                <div className="inline-flex flex-col gap-1 items-center">
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Badge className="cursor-pointer bg-slate-700 text-slate-200 border-slate-600" title="Voir le détail">
                                        {formatEUR(bd.total)}
                                      </Badge>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-80 bg-slate-900 border-slate-700 text-slate-200">
                                      <div className="flex flex-col gap-2">
                                        <div className="flex justify-between items-center">
                                          <span className="text-slate-400">Sous-total</span>
                                          <span className="font-medium">{formatEUR(bd.subtotal)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                          <span className="text-slate-400">Remise</span>
                                          <span className="font-medium text-orange-300">-{formatEUR(bd.discount)}</span>
                                        </div>
                                        {bd.discount > 0 && codeLabel && (
                                          <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Code</span>
                                            <span className="font-medium text-slate-300">
                                              {codeLabel}
                                              {typeof codeObj?.percent === 'number' ? ` • ${codeObj.percent}%` : ''}
                                              {typeof codeObj?.flat === 'number' ? ` • ${formatEUR(codeObj.flat)}` : ''}
                                            </span>
                                          </div>
                                        )}
                                        <div className="h-px bg-slate-700" />
                                        <div className="flex justify-between items-center">
                                          <span className="text-slate-200">Total</span>
                                          <span className="font-bold text-emerald-300">{formatEUR(bd.total)}</span>
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setOrderEditingId(o.id);
                                  setOrderEditItems((o.items || []).map(it => ({ name: it.name, quantity: it.quantity })));
                                  setOrderEditComment(o.comment || '');
                                  setOrderEditOpen(true);
                                }}
                                className="text-slate-300 hover:text-white hover:bg-slate-700"
                                aria-label="Modifier commande"
                                title="Modifier"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (!ordersEventId) return;
                                  if (!window.confirm('Supprimer cette commande ?')) return;
                                  deleteOrder(ordersEventId, o.id);
                                }}
                                className="text-slate-300 hover:text-red-400 hover:bg-red-500/10"
                                aria-label="Supprimer commande"
                                title="Supprimer"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                  {paidOrders.length > 0 && (
                    <TableRow className="border-t-2 border-slate-700">
                      <TableCell className="font-semibold text-white">Total</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell className="text-center">
                        <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                          {formatEUR(paidOrders.reduce((sum, o) => sum + orderTotal(o), 0))}
                        </Badge>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {paidOrders.length === 0 ? (
                <div className="p-6 text-center text-slate-400">Aucun historique</div>
              ) : (
                paidOrders.map((o) => {
                  const tinfo = tables.find(t => t.id === o.tableId);
                  return (
                    <AdminCard key={o.id}>
                      <CardHeader className="pb-3">
                        <div className="flex gap-2 items-center">
                          <CardTitle className="text-lg text-white truncate">{tinfo?.name || '—'}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                            <div className="text-xs tracking-wide uppercase text-slate-400">Articles</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(o.items || []).map((it, idx) => {
                                const isDrink = (ordersTypeMap[it.name] || '') === 'drink';
                                return (
                                  <Badge key={idx} className={cn(isDrink ? "text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30" : "bg-slate-700 text-slate-200 border-slate-600")}>
                                    {it.name} × {it.quantity}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                          <div className="p-3 min-w-0 rounded-lg border border-slate-700 bg-slate-800/40">
                            <div className="text-xs tracking-wide uppercase text-slate-400">Paiement & Total</div>
                            <div className="mt-1 text-sm text-slate-300">{formatDateTime(o.paidAt)}</div>
                            <div className="mt-1 font-semibold text-white">
                              {(() => {
                                const bd = orderBreakdown(o);
                                const tinfo = (tables || []).find(t => t.id === o.tableId);
                                const codeObj = tinfo?.codeId ? (codes || []).find(c => c.id === tinfo.codeId) : null;
                                const codeLabel = codeObj?.code || null;
                                return (
                                  <div className="inline-flex flex-col gap-1 items-start">
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Badge className="cursor-pointer bg-slate-700 text-slate-200 border-slate-600" title="Voir le détail">
                                          {formatEUR(bd.total)}
                                        </Badge>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-80 bg-slate-900 border-slate-700 text-slate-200">
                                        <div className="flex flex-col gap-2">
                                          <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Sous-total</span>
                                            <span className="font-medium">{formatEUR(bd.subtotal)}</span>
                                          </div>
                                          <div className="flex justify-between items-center">
                                            <span className="text-slate-400">Remise</span>
                                            <span className="font-medium text-orange-300">-{formatEUR(bd.discount)}</span>
                                          </div>
                                          <div className="h-px bg-slate-700" />
                                          <div class className="flex justify-between items-center">
                                            <span className="text-slate-200">Total</span>
                                            <span className="font-bold text-emerald-300">{formatEUR(bd.total)}</span>
                                          </div>
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                    {bd.discount > 0 && codeLabel && (
                                      <Badge className="bg-slate-700 text-slate-300 border-slate-600">
                                        Code: {codeLabel}
                                        {typeof codeObj?.percent === 'number' ? ` • ${codeObj.percent}%` : ''}
                                        {typeof codeObj?.flat === 'number' ? ` • ${formatEUR(codeObj.flat)}` : ''}
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                      <AdminCardActions>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setOrderEditingId(o.id);
                            setOrderEditItems((o.items || []).map(it => ({ name: it.name, quantity: it.quantity })));
                            setOrderEditComment(o.comment || '');
                            setOrderEditOpen(true);
                          }}
                          className="text-slate-300 hover:text-white hover:bg-slate-700"
                          title="Modifier"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (!ordersEventId) return;
                            if (!window.confirm('Supprimer cette commande ?')) return;
                            deleteOrder(ordersEventId, o.id);
                          }}
                          className="text-slate-300 hover:text-red-400 hover:bg-red-500/10"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </AdminCardActions>
                    </AdminCard>
                  );
                })
              )}
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={orderEditOpen}
        onOpenChange={(open) => {
          setOrderEditOpen(open);
          if (!open) {
            setOrderEditingId('');
            setOrderEditItems([]);
            setOrderEditComment('');
            setOrderEditPriceMap({});
            setOrderEditMeta({
              subtotal: 0,
              discountAmount: 0,
              discountPercent: null,
              discountFlat: null,
              total: 0,
              appliedCode: null,
            });
          }
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-[100vw] max-w-[100vw] w-screen h-screen p-0 overflow-y-auto rounded-none">
          <div className="flex flex-col h-full">
            <div className="p-3 border-b border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-lg text-white">Modifier la commande</DialogTitle>
                <DialogDescription className="text-sm text-slate-300">
                  Interface tactile: ajustez les quantités avec +/- et validez.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap gap-2 mt-3">
                {orderEditMeta.appliedCode ? (
                  <Badge className="bg-slate-700 text-slate-200 border-slate-600 text-xs px-1.5 py-0.5">
                    Code: {orderEditMeta.appliedCode}
                  </Badge>
                ) : null}
                <Badge className="bg-slate-700 text-slate-200 border-slate-600 text-xs px-1.5 py-0.5">
                  Sous-total: {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(orderEditMeta.subtotal || 0)}
                </Badge>
                <Badge className={cn(
                  "border-slate-600 text-xs px-1.5 py-0.5",
                  (orderEditMeta.discountAmount || 0) > 0 ? "text-orange-300 bg-orange-500/20 border-orange-500/30" : "bg-slate-700 text-slate-200"
                )}>
                  Remise: {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(orderEditMeta.discountAmount || 0)}
                </Badge>
                <Badge className="text-emerald-300 bg-emerald-500/20 border-emerald-500/30 text-xs px-1.5 py-0.5">
                  Total: {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(orderEditMeta.total || 0)}
                </Badge>
              </div>
            </div>
            <div className="grid flex-1 min-h-0 grid-rows-[auto_1fr] gap-0 lg:grid-rows-1 lg:grid-cols-12">
              <div className="overflow-y-auto row-span-1 p-3 lg:col-span-8">
                <Tabs value={editOrderTab} onValueChange={setEditOrderTab}>
                  <TabsList className="mb-2 h-8 bg-slate-800 text-slate-300">
                    <TabsTrigger value="foods" className="px-2 py-1 text-xs data-[state=active]:bg-white data-[state=active]:text-black">Plats</TabsTrigger>
                    <TabsTrigger value="drinks" className="px-2 py-1 text-xs data-[state=active]:bg-white data-[state=active]:text-black">Boissons</TabsTrigger>
                  </TabsList>
                  <TabsContent value="foods">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {['starter', 'main', 'dessert'].map(type => (
                        <div key={type} className="rounded-lg">
                          <div className="flex gap-2 items-center mb-2">
                            <Utensils size={16} className="text-blue-400" />
                            <div className="text-sm font-semibold text-white">
                              {type === 'starter' ? 'Entrées' : type === 'main' ? 'Plats' : 'Desserts'}
                            </div>
                          </div>
                          <div className="grid gap-1.5">
                            {(editGroups[type] || []).map(d => {
                              const existing = (orderEditItems || []).find(x => x.name === d.name);
                              const qty = Number(existing?.quantity || 0);
                              return (
                                <button
                                  key={d.name}
                                  onClick={() => {
                                    setOrderEditItems(prev => {
                                      const idx = prev.findIndex(p => p.name === d.name);
                                      if (idx >= 0) {
                                        const next = [...prev];
                                        next[idx] = { ...next[idx], quantity: (next[idx].quantity || 0) + 1 };
                                        return next;
                                      }
                                      return [...prev, { name: d.name, quantity: 1 }];
                                    });
                                    setOrderEditPriceMap(prev => {
                                      const p = typeof d.price === 'number' ? d.price : 0;
                                      if (prev && prev[d.name] != null) return prev;
                                      return { ...(prev || {}), [d.name]: p };
                                    });
                                  }}
                                  className={cn(
                                    "relative w-full rounded-lg border border-slate-700 bg-slate-800/40 px-3 py-2 text-left transition hover:bg-slate-800",
                                    qty > 0 && "ring-2 ring-blue-500/50"
                                  )}
                                >
                                  <div className="flex gap-3 justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium truncate text-slate-200">{d.name}</div>
                                      {d.description && <div className="text-xs truncate text-slate-400">{d.description}</div>}
                                    </div>
                                    {qty > 0 && (
                                      <span className="inline-flex justify-center items-center w-7 h-7 text-xs font-semibold text-white bg-blue-600 rounded-full shrink-0">
                                        {qty}
                                      </span>
                                    )}
                                  </div>
                                  {typeof d.price === 'number' && (
                                    <div className="mt-1.5">
                                      <Badge className="bg-slate-700 text-slate-200 border-slate-600 text-xs px-1.5 py-0.5">
                                        {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(d.price || 0)}
                                      </Badge>
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                            {(editGroups[type] || []).length === 0 && (
                              <div className="text-xs text-slate-400">Aucun élément</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="drinks">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {(orderEditMenu?.dishes || []).filter(d => (d.type || '').toLowerCase() === 'drink').map(d => {
                        const existing = (orderEditItems || []).find(x => x.name === d.name);
                        const qty = Number(existing?.quantity || 0);
                        const fmt = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(n);
                        return (
                          <button
                            key={d.name}
                            onClick={() => {
                              setOrderEditItems(prev => {
                                const idx = prev.findIndex(p => p.name === d.name);
                                if (idx >= 0) {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], quantity: (next[idx].quantity || 0) + 1 };
                                  return next;
                                }
                                return [...prev, { name: d.name, quantity: 1 }];
                              });
                              setOrderEditPriceMap(prev => {
                                const p = typeof d.price === 'number' ? d.price : 0;
                                if (prev && prev[d.name] != null) return prev;
                                return { ...(prev || {}), [d.name]: p };
                              });
                            }}
                            className={cn("w-full text-left rounded-lg border border-slate-700 px-3 py-2 bg-slate-800/40 hover:bg-slate-800 transition", qty > 0 && "ring-2 ring-fuchsia-500/40")}
                          >
                            <div className="flex gap-3 justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate text-slate-200">{d.name}</div>
                                {d.description && <div className="text-xs truncate text-slate-400">{d.description}</div>}
                              </div>
                              {qty > 0 && <span className="inline-flex justify-center items-center w-7 h-7 text-xs font-semibold text-white bg-fuchsia-600 rounded-full">{qty}</span>}
                            </div>
                            {typeof d.price === 'number' && (
                              <div className="mt-1.5">
                                <Badge className="text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30 text-xs px-1.5 py-0.5">{fmt(d.price)}</Badge>
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {((orderEditMenu?.dishes || []).filter(d => (d.type || '').toLowerCase() === 'drink').length === 0) && <div className="text-xs text-slate-400">Aucune boisson</div>}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
              <div className="flex flex-col row-span-1 h-full min-h-0 border-t lg:col-span-4 lg:border-l border-slate-800">
                <div className="flex flex-col gap-3 p-3 h-full min-h-0">
                  <div className="flex flex-col flex-1 min-h-0">
                    <div className="mb-1 text-xs tracking-wide uppercase text-slate-400">Articles</div>
                    <div className="overflow-y-auto flex-1 pr-1 space-y-1.5">
                      {(orderEditItems || []).length === 0 ? (
                        <div className="text-xs text-slate-400">Aucun article</div>
                      ) : (
                        (orderEditItems || []).map((it, idx) => {
                          const unit = Number(orderEditPriceMap?.[it.name] || 0);
                          return (
                            <div key={idx} className="flex gap-2 justify-between items-center px-2 py-1.5 rounded-lg border border-slate-700 bg-slate-900/40">
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate text-slate-200">{it.name}</div>
                              </div>
                              <div className="flex gap-2 items-center">
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="w-7 h-7 border-slate-600 text-slate-200 hover:bg-slate-700"
                                  onClick={() => {
                                    setOrderEditItems(prev => prev.map((p, i) => i === idx ? { ...p, quantity: Math.max(0, (p.quantity || 0) - 1) } : p));
                                  }}
                                  aria-label="Diminuer"
                                >
                                  <Minus size={14} />
                                </Button>
                                <span className="w-7 text-sm text-center text-slate-200">{it.quantity || 0}</span>
                                <Button
                                  size="icon"
                                  className="w-7 h-7 text-white bg-blue-600 hover:bg-blue-700"
                                  onClick={() => {
                                    setOrderEditItems(prev => prev.map((p, i) => i === idx ? { ...p, quantity: (p.quantity || 0) + 1 } : p));
                                  }}
                                  aria-label="Augmenter"
                                >
                                  <Plus size={14} />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="w-7 h-7 border-slate-600 text-slate-200 hover:bg-slate-700"
                                  onClick={() => {
                                    setOrderEditItems(prev => prev.map((p, i) => i === idx ? { ...p, quantity: 0 } : p));
                                  }}
                                  title="Retirer"
                                  aria-label="Retirer"
                                >
                                  <Trash2 size={14} />
                                </Button>
                                <div className="ml-2 min-w-[4.5rem] text-right font-semibold text-slate-100 text-sm">
                                  {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(unit * Number(it.quantity || 0))}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block mb-1 text-sm text-slate-300">Commentaire</label>
                    <Textarea
                      value={orderEditComment}
                      onChange={(e) => setOrderEditComment(e.target.value)}
                      placeholder="Ex: Allergies, préférences, etc."
                      className="min-h-[64px] text-sm text-white bg-slate-900 border-slate-700 placeholder-slate-400"
                    />
                  </div>
                  <div className="rounded-lg border border-slate-700 bg-slate-900/40">
                    <div className="p-3">
                      <div className="mb-2 text-xs tracking-wide uppercase text-slate-400">Détail</div>
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400">Sous-total</span>
                          <span className="font-medium text-white">
                            {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(orderEditMeta.subtotal || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400">Remise</span>
                          <span className={cn("font-medium", (orderEditMeta.discountAmount || 0) > 0 ? "text-orange-300" : "text-slate-300")}>
                            -{new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(orderEditMeta.discountAmount || 0)}
                          </span>
                        </div>
                        <div className="h-px bg-slate-700" />
                        <div className="flex justify-between items-center">
                          <span className="text-slate-200">Total</span>
                          <span className="text-lg font-bold text-emerald-300">
                            {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(orderEditMeta.total || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-3 border-t border-slate-800">
              <DialogFooter>
                <div className="flex justify-between items-center w-full">
                  <Button
                    variant="destructive"
                    onClick={() => {
                      if (!ordersEventId || !orderEditingId) return;
                      if (!window.confirm('Supprimer cette commande ?')) return;
                      deleteOrder(ordersEventId, orderEditingId);
                      setOrderEditOpen(false);
                      setOrderEditingId('');
                    }}
                    className="px-5 h-10 text-base"
                  >
                    Supprimer la commande
                  </Button>
                  <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      if (!ordersEventId || !orderEditingId) return;
                      const items = (orderEditItems || []).filter(it => (it.quantity || 0) > 0);
                      const comment = (orderEditComment || '').trim() || undefined;
                      updateOrder(ordersEventId, orderEditingId, { items, comment });
                      setOrderEditOpen(false);
                      setOrderEditingId('');
                    }}
                    className="px-5 h-10 text-base text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    Enregistrer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOrderEditOpen(false);
                      setOrderEditingId('');
                    }}
                    className="px-5 h-10 text-base border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                  >
                    Annuler
                  </Button>
                  </div>
                </div>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={payConfirmOpen}
        onOpenChange={(open) => {
          setPayConfirmOpen(open);
          if (!open) {
            setPayTarget(null);
          }
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmer le paiement</DialogTitle>
            <DialogDescription className="text-slate-300">
              Vérifiez le montant avant de marquer la commande comme payée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {payTarget && (() => {
              const bd = orderBreakdown(payTarget);
              return (
                <>
                  <div className="overflow-y-auto max-h-60 rounded-lg border bg-slate-900/60 border-slate-700">
                    <div className="p-3">
                      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 md:gap-4 text-sm">
                        <div className="text-slate-400">Article</div>
                        <div className="pl-3 text-right border-l text-slate-400 md:pl-4 border-slate-800">Qté</div>
                        <div className="pl-3 text-right border-l text-slate-400 md:pl-4 border-slate-800">Prix</div>
                        <div className="pl-3 text-right border-l text-slate-400 md:pl-4 border-slate-800">Total</div>
                      </div>
                      {(payTarget.items || []).map((it, idx) => {
                        const qty = Number(it.quantity || 0);
                        const unit = Number(it.price || 0);
                        const line = Math.round(qty * unit * 100) / 100;
                        return (
                          <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 md:gap-4 py-1.5 border-t first:border-t-0 border-slate-800">
                            <div className="truncate text-slate-200">{it.name}</div>
                            <div className="pl-3 text-right border-l text-slate-300 md:pl-4 border-slate-800">{qty}</div>
                            <div className="pl-3 text-right border-l text-slate-300 md:pl-4 border-slate-800">{formatEUR(unit)}</div>
                            <div className="pl-3 text-right text-white border-l md:pl-4 border-slate-800">{formatEUR(line)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {(() => {
                    const tinfo = (tables || []).find(t => t.id === payTarget.tableId);
                    const currentId = tinfo?.codeId ? String(tinfo.codeId) : 'none';
                    return (
                      <div className="mt-3">
                        <div className="mb-1 text-xs tracking-wide uppercase text-slate-400">Code de réduction</div>
                        <div className="flex gap-2 items-center">
                          <Select
                            value={currentId}
                            onValueChange={async (v) => {
                              if (!ordersEventId || !tinfo?.id) return;
                              try {
                                const nextCodeId = (v === 'none') ? '' : v;
                                await updateTable(ordersEventId, tinfo.id, { codeId: nextCodeId });
                              } catch {}
                            }}
                          >
                            <SelectTrigger className="w-56 text-white bg-slate-900 border-slate-700">
                              <SelectValue placeholder="Aucun" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700">
                              <SelectItem value="none" className="text-white">Aucun</SelectItem>
                              {(codes || []).map(c => (
                                <SelectItem key={c.id} value={String(c.id)} className="text-white">
                                  {c.code} {(typeof c.percent === 'number' && c.percent > 0) ? `• ${c.percent}%` : (typeof c.flat === 'number' && c.flat > 0) ? `• ${formatEUR(c.flat)}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {tinfo?.codeId && (
                            <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                              {(codes || []).find(c => c.id === tinfo.codeId)?.code || ''}
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="mt-3 space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Sous-total</span>
                      <span className="font-medium text-white">{formatEUR(bd.subtotal)}</span>
                    </div>
                    {bd.discount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400">Remise</span>
                        <span className="font-medium text-orange-300">-{formatEUR(bd.discount)}</span>
                      </div>
                    )}
                    <div className="h-px bg-slate-700" />
                    <div className="flex justify-between items-center">
                      <span className="text-slate-200">Total</span>
                      <span className="font-bold text-emerald-300">{formatEUR(bd.total)}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (!ordersEventId || !payTarget) return;
                  payOrder(ordersEventId, payTarget.id);
                  setPayConfirmOpen(false);
                  setPayTarget(null);
                }}
                className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Confirmer
              </Button>
              <Button
                variant="outline"
                onClick={() => { setPayConfirmOpen(false); setPayTarget(null); }}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Annuler
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={sendConfirmOpen}
        onOpenChange={(open) => {
          setSendConfirmOpen(open);
          if (!open) {
            setSendTarget(null);
          }
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Envoyer la commande ?</DialogTitle>
            <DialogDescription className="text-slate-300">
              Confirmez l’envoie de la commande en cuisine.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {sendTarget && (
              <>
                <div className="overflow-y-auto max-h-64 rounded-lg border bg-slate-900/60 border-slate-700">
                  <div className="p-3">
                    <div className="grid grid-cols-[1fr_auto] gap-3 md:gap-4 text-xs">
                      <div className="text-slate-400">Article</div>
                      <div className="pl-3 text-right border-l text-slate-400 md:pl-4 border-slate-800">Qté</div>
                    </div>
                    {(sendTarget.items || []).map((it, idx) => {
                      const qty = Number(it.quantity || 0);
                      return (
                        <div key={idx} className="grid grid-cols-[1fr_auto] gap-3 md:gap-4 py-1.5 border-t border-slate-800">
                          <div className="truncate text-slate-200">{it.name}</div>
                          <div className="pl-3 text-right border-l text-slate-300 md:pl-4 border-slate-800">{qty}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (!ordersEventId || !sendTarget) return;
                  const nextStatus = sendTarget.paidAt ? 'paid' : 'sent';
                  updateOrder(ordersEventId, sendTarget.id, { status: nextStatus });
                  setSendConfirmOpen(false);
                  setSendTarget(null);
                }}
                className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Envoyer
              </Button>
              <Button
                variant="outline"
                onClick={() => { setSendConfirmOpen(false); setSendTarget(null); }}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Annuler
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={payManyOpen}
        onOpenChange={(open) => {
          setPayManyOpen(open);
          if (!open) {
            setPayManyOrders([]);
          }
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmer le paiement</DialogTitle>
            <DialogDescription className="text-slate-300">
              Confirmez le paiement des commandes sélectionnées.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(() => {
              const subtotals = (payManyOrders || []).map(o => orderBreakdown(o).subtotal);
              const totals = (payManyOrders || []).map(o => orderBreakdown(o).total);
              const discounts = (payManyOrders || []).map(o => orderBreakdown(o).discount);
              const subtotalSum = subtotals.reduce((a, b) => a + b, 0);
              const totalSum = totals.reduce((a, b) => a + b, 0);
              const discountSum = discounts.reduce((a, b) => a + b, 0);
              return (
                <>
                  <div className="overflow-y-auto max-h-64 rounded-lg border bg-slate-900/60 border-slate-700">
                    <div className="p-3 space-y-3">
                      {(payManyOrders || []).map((ord) => {
                        const bd = orderBreakdown(ord);
                        const tinfo = (tables || []).find(t => t.id === ord.tableId);
                        return (
                          <div key={ord.id} className="border-t border-slate-800">
                            <div className="flex justify-between items-center p-2 text-sm bg-slate-900/70">
                              <div className="text-white">{tinfo?.name || '—'}</div>
                              <div className="inline-flex gap-1 items-center">
                                <Badge className="bg-slate-700 text-slate-200 border-slate-600">{formatEUR(bd.subtotal)}</Badge>
                                {bd.discount > 0 && (
                                  <Badge className="text-orange-300 bg-orange-500/20 border-orange-500/30">-{formatEUR(bd.discount)}</Badge>
                                )}
                                <Badge className="text-emerald-300 bg-emerald-500/20 border-emerald-500/30">{formatEUR(bd.total)}</Badge>
                              </div>
                            </div>
                          <div className="p-2">
                            <div className="mb-1 text-xs tracking-wide uppercase text-slate-400">Code de réduction</div>
                            <div className="flex gap-2 items-center">
                              <Select
                                value={tinfo?.codeId ? String(tinfo.codeId) : 'none'}
                                onValueChange={async (v) => {
                                  if (!ordersEventId || !tinfo?.id) return;
                                  try {
                                    const nextCodeId = (v === 'none') ? '' : v;
                                    await updateTable(ordersEventId, tinfo.id, { codeId: nextCodeId });
                                  } catch {}
                                }}
                              >
                                <SelectTrigger className="w-48 text-white bg-slate-900 border-slate-700">
                                  <SelectValue placeholder="Aucun" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-700">
                                  <SelectItem value="none" className="text-white">Aucun</SelectItem>
                                  {(codes || []).map(c => (
                                    <SelectItem key={c.id} value={String(c.id)} className="text-white">
                                      {c.code} {(typeof c.percent === 'number' && c.percent > 0) ? `• ${c.percent}%` : (typeof c.flat === 'number' && c.flat > 0) ? `• ${formatEUR(c.flat)}` : ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {tinfo?.codeId && (
                                <Badge className="bg-slate-700 text-slate-200 border-slate-600">
                                  {(codes || []).find(c => c.id === tinfo.codeId)?.code || ''}
                                </Badge>
                              )}
                            </div>
                          </div>
                            <div className="p-2">
                              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 md:gap-4 text-xs">
                                <div className="text-slate-400">Article</div>
                                <div className="pl-3 text-right border-l text-slate-400 md:pl-4 border-slate-800">Qté</div>
                                <div className="pl-3 text-right border-l text-slate-400 md:pl-4 border-slate-800">Prix</div>
                                <div className="pl-3 text-right border-l text-slate-400 md:pl-4 border-slate-800">Total</div>
                              </div>
                              {(ord.items || []).map((it, idx) => {
                                const qty = Number(it.quantity || 0);
                                const unit = Number(it.price || 0);
                                const line = Math.round(qty * unit * 100) / 100;
                                return (
                                  <div key={`${ord.id}-${idx}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 md:gap-4 py-1.5 border-t border-slate-800">
                                    <div className="truncate text-slate-200">{it.name}</div>
                                    <div className="pl-3 text-right border-l text-slate-300 md:pl-4 border-slate-800">{qty}</div>
                                    <div className="pl-3 text-right border-l text-slate-300 md:pl-4 border-slate-800">{formatEUR(unit)}</div>
                                    <div className="pl-3 text-right text-white border-l md:pl-4 border-slate-800">{formatEUR(line)}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Sous-total</span>
                      <span className="font-medium text-white">{formatEUR(subtotalSum)}</span>
                    </div>
                    {discountSum > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400">Remise</span>
                        <span className="font-medium text-orange-300">-{formatEUR(discountSum)}</span>
                      </div>
                    )}
                    <div className="h-px bg-slate-700" />
                    <div className="flex justify-between items-center">
                      <span className="text-slate-200">Total</span>
                      <span className="font-bold text-emerald-300">{formatEUR(totalSum)}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (!ordersEventId || !(payManyOrders || []).length) return;
                  (payManyOrders || []).forEach(o => finishOrder(ordersEventId, o.id));
                  setPayManyOpen(false);
                  setPayManyOrders([]);
                }}
                className="text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Confirmer
              </Button>
              <Button
                variant="outline"
                onClick={() => { setPayManyOpen(false); setPayManyOrders([]); }}
                className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Annuler
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={addOrderOpen}
        onOpenChange={(open) => {
          setAddOrderOpen(open);
          if (!open) {
            setAddOrderMenu({ dishes: [] });
            setAddOrderQuantities({});
            setAddOrderComment('');
            setAddOrderTableSlug('');
            setAddOrderSubmitting(false);
            setAddOrderSearch('');
          }
        }}
      >
        <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-[100vw] max-w-[100vw] w-screen h-screen p-0 overflow-y-auto rounded-none">
          <div className="flex flex-col h-full">
            <div className="p-3 border-b border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-lg text-white">Ajouter une commande</DialogTitle>
                <DialogDescription className="text-sm text-slate-300">
                  Sélection rapide sans clavier: touchez un plat pour l’ajouter au panier.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 mt-3 sm:grid-cols-2">
                <div>
                  <label className="block mb-1 text-sm text-slate-300">Table</label>
                  <Select value={addOrderTableSlug} onValueChange={setAddOrderTableSlug}>
                    <SelectTrigger className="w-full h-9 text-sm text-white bg-slate-900 border-slate-700">
                      <SelectValue placeholder="Sélectionner une table" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {(tables || []).map(t => (
                        <SelectItem key={t.id} value={t.slug} className="text-white">
                          {t.name} • #{t.number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedCode && (
                    <div className="mt-2">
                      <Badge className="bg-slate-700 text-slate-200 border-slate-600 text-xs px-1.5 py-0.5">
                        Code: {selectedCode.code}
                        {typeof selectedCode.percent === 'number' ? ` • ${selectedCode.percent}%` : ''}
                        {typeof selectedCode.flat === 'number' ? ` • ${new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(selectedCode.flat)}` : ''}
                      </Badge>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-1 text-sm text-slate-300">Commentaire</label>
                  <Textarea
                    value={addOrderComment}
                    onChange={(e) => setAddOrderComment(e.target.value)}
                    placeholder="Ex: Allergies, préférences, etc."
                    className="min-h-[40px] text-sm text-white bg-slate-900 border-slate-700 placeholder-slate-400"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block mb-1 text-sm text-slate-300">Rechercher un plat</label>
                  <div className="relative">
                    <Input
                      value={addOrderSearch}
                      onChange={(e) => setAddOrderSearch(e.target.value)}
                      placeholder="Tapez le nom ou la description"
                      className="pr-24 h-9 text-sm text-white bg-slate-900 border-slate-700 placeholder-slate-400"
                    />
                    {addOrderSearch ? (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <Button
                          variant="outline"
                          className="px-2 h-7 text-xs border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                          onClick={() => setAddOrderSearch('')}
                          title="Effacer"
                        >
                          Effacer
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid flex-1 min-h-0 grid-rows-[auto_1fr] gap-0 lg:grid-rows-1 lg:grid-cols-12">
              <div className="overflow-y-auto row-span-1 p-3 lg:col-span-8">
                <Tabs value={addOrderTab} onValueChange={setAddOrderTab}>
                  <TabsList className="mb-2 h-8 bg-slate-800 text-slate-300">
                    <TabsTrigger value="foods" className="px-2 py-1 text-xs data-[state=active]:bg-white data-[state=active]:text-black">Plats</TabsTrigger>
                    <TabsTrigger value="drinks" className="px-2 py-1 text-xs data-[state=active]:bg-white data-[state=active]:text-black">Boissons</TabsTrigger>
                  </TabsList>
                  <TabsContent value="foods">
                    <div className="grid grid-cols-1 auto-rows-max gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {['starter', 'main', 'dessert'].map(type => (
                    <div key={type} className="rounded-lg">
                      <div className="flex gap-2 items-center mb-2">
                        <Utensils size={16} className="text-blue-400" />
                        <div className="text-sm font-semibold text-white">
                          {type === 'starter' ? 'Entrées' : type === 'main' ? 'Plats' : 'Desserts'}
                        </div>
                      </div>
                      <div className="grid gap-1.5">
                        {(viewGroups[type] || []).map(d => {
                          const qty = Number(addOrderQuantities[d.name] || 0);
                          return (
                            <button
                              key={d.name}
                              onClick={() => {
                                setAddOrderQuantities(prev => ({ ...prev, [d.name]: (prev[d.name] || 0) + 1 }));
                              }}
                              className={cn(
                                "w-full text-left rounded-lg border border-slate-700 px-3 py-2 bg-slate-800/40 hover:bg-slate-800 transition relative",
                                qty > 0 && "ring-2 ring-blue-500/50"
                              )}
                            >
                              <div className="flex gap-3 justify-between items-start">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate text-slate-200">{highlightText(d.name)}</div>
                                  {d.description && <div className="text-xs truncate text-slate-400">{highlightText(d.description)}</div>}
                                </div>
                                {qty > 0 && (
                                  <span className="inline-flex justify-center items-center w-7 h-7 text-xs font-semibold text-white bg-blue-600 rounded-full shrink-0">
                                    {qty}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {typeof d.price === 'number' && (() => {
                                  const p = d.price || 0;
                                  const perc = typeof selectedCode?.percent === 'number' ? selectedCode.percent : 0;
                                  const flat = typeof selectedCode?.flat === 'number' ? selectedCode.flat : 0;
                                  const discountAmt = Math.max(0, (perc > 0 ? p * (perc / 100) : 0) + (flat > 0 ? flat : 0));
                                  const discounted = Math.max(0, Math.round((p - discountAmt) * 100) / 100);
                                  const fmt = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(n);
                                  return selectedCode ? (
                                    <>
                                      <Badge className="line-through bg-slate-700 text-slate-400 border-slate-600 text-xs px-1.5 py-0.5">{fmt(p)}</Badge>
                                      <Badge className="text-orange-300 bg-orange-500/20 border-orange-500/30 text-xs px-1.5 py-0.5">
                                        -{perc > 0 ? `${perc}%` : ''}{perc > 0 && flat > 0 ? ' • ' : ''}{flat > 0 ? fmt(flat) : ''}
                                      </Badge>
                                      <Badge className="text-emerald-300 bg-emerald-500/20 border-emerald-500/30 text-xs px-1.5 py-0.5">{fmt(discounted)}</Badge>
                                    </>
                                  ) : (
                                    <Badge className="bg-slate-700 text-slate-200 border-slate-600 text-xs px-1.5 py-0.5">{fmt(p)}</Badge>
                                  );
                                })()}
                              </div>
                            </button>
                          );
                        })}
                        {(viewGroups[type] || []).length === 0 && (
                          <div className="text-xs text-slate-400">Aucun élément</div>
                        )}
                      </div>
                    </div>
                  ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="drinks">
                    <div className="grid grid-cols-1 auto-rows-max gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {(addOrderMenu?.dishes || []).filter(d => (d.type || '').toLowerCase() === 'drink').map(d => {
                        const qty = Number(addOrderQuantities[d.name] || 0);
                        const fmt = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(n);
                        return (
                          <button
                            key={d.name}
                            onClick={() => setAddOrderQuantities(prev => ({ ...prev, [d.name]: (prev[d.name] || 0) + 1 }))}
                            className={cn("w-full text-left rounded-lg border border-slate-700 px-3 py-2 bg-slate-800/40 hover:bg-slate-800 transition", qty > 0 && "ring-2 ring-fuchsia-500/40")}
                          >
                            <div className="flex gap-3 justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate text-slate-200">{highlightText(d.name)}</div>
                                {d.description && <div className="text-xs truncate text-slate-400">{highlightText(d.description)}</div>}
                              </div>
                              {qty > 0 && <span className="inline-flex justify-center items-center w-7 h-7 text-xs font-semibold text-white bg-fuchsia-600 rounded-full">{qty}</span>}
                            </div>
                            {typeof d.price === 'number' && (
                              <div className="mt-1.5">
                                <Badge className="text-fuchsia-300 bg-fuchsia-500/20 border-fuchsia-500/30 text-xs px-1.5 py-0.5">{fmt(d.price)}</Badge>
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {((addOrderMenu?.dishes || []).filter(d => (d.type || '').toLowerCase() === 'drink').length === 0) && <div className="text-xs text-slate-400">Aucune boisson</div>}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
              <div className="flex flex-col row-span-1 min-h-0 max-h-full border-t lg:col-span-4 lg:border-l border-slate-800">
                {(() => {
                  const entries = Object.entries(addOrderQuantities).filter(([_, q]) => (q || 0) > 0);
                  const priceOf = (name) => {
                    const dish = (addOrderMenu?.dishes || []).find(it => it.name === name);
                    return dish && typeof dish.price === 'number' ? dish.price : 0;
                  };
                  const subtotal = entries.reduce((s, [name, q]) => s + priceOf(name) * Number(q || 0), 0);
                  const perc = typeof selectedCode?.percent === 'number' ? selectedCode.percent : 0;
                  const flat = typeof selectedCode?.flat === 'number' ? selectedCode.flat : 0;
                  const unitDiscount = (p) => Math.max(0, (perc > 0 ? p * (perc / 100) : 0) + (flat > 0 ? flat : 0));
                  const discountSum = entries.reduce((s, [name, q]) => {
                    const p = priceOf(name);
                    return s + unitDiscount(p) * Number(q || 0);
                  }, 0);
                  const totalAfter = Math.max(0, Math.round((subtotal - discountSum) * 100) / 100);
                  return (
                    <div className="flex flex-col min-h-0">
                      <div className="p-3">
                        <div className="text-base font-semibold text-white">Panier</div>
                        <div className="text-xs text-slate-400">Touchez un plat pour l’ajouter, ajustez ici si nécessaire.</div>
                        {selectedCode && (
                          <div className="mt-2">
                            <Badge className="bg-slate-700 text-slate-200 border-slate-600 text-xs px-1.5 py-0.5">
                              Code: {selectedCode.code}
                              {typeof selectedCode.percent === 'number' ? ` • ${selectedCode.percent}%` : ''}
                              {typeof selectedCode.flat === 'number' ? ` • ${new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(selectedCode.flat)}` : ''}
                            </Badge>
                          </div>
                        )}
                      </div>
                      <div className="overflow-y-auto flex-1 px-3 pr-1 pb-3 space-y-1.5">
                        {entries.length === 0 ? (
                          <div className="text-xs text-slate-400">Aucun article sélectionné</div>
                        ) : (
                          entries.map(([name, q]) => {
                            const unit = priceOf(name);
                            const disc = unitDiscount(unit);
                            const unitAfter = Math.max(0, Math.round((unit - disc) * 100) / 100);
                            const line = unitAfter * Number(q || 0);
                            return (
                              <div key={name} className="flex gap-2 justify-between items-center px-2 py-1.5 rounded-lg border border-slate-700 bg-slate-900/40">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium truncate text-slate-200">{name}</div>
                                  <div className="text-xs text-slate-400">Qté: {q}</div>
                                </div>
                                <div className="flex gap-2 items-center">
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    className="w-7 h-7 border-slate-600 text-slate-200 hover:bg-slate-700"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddOrderQuantities(prev => {
                                        const next = { ...prev, [name]: Math.max(0, (prev[name] || 0) - 1) };
                                        if (next[name] === 0) { delete next[name]; }
                                        return next;
                                      });
                                    }}
                                  >
                                    <Minus size={14} />
                                  </Button>
                                  <span className="w-7 text-sm text-center text-slate-200">{q}</span>
                                  <Button
                                    size="icon"
                                    className="w-7 h-7 text-white bg-blue-600 hover:bg-blue-700"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddOrderQuantities(prev => ({ ...prev, [name]: (prev[name] || 0) + 1 }));
                                    }}
                                  >
                                    <Plus size={14} />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="outline"
                                    className="w-7 h-7 border-slate-600 text-slate-200 hover:bg-slate-700"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddOrderQuantities(prev => {
                                        const next = { ...prev };
                                        delete next[name];
                                        return next;
                                      });
                                    }}
                                    title="Retirer"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                  <div className="ml-2 min-w-[4.5rem] text-right font-semibold text-slate-100 text-sm">
                                    {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(line)}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="p-3 mt-auto border-t border-slate-800">
                        {!selectedCode ? (
                          <div className="flex justify-between items-center">
                            <div className="text-slate-300">Total</div>
                            <div className="text-lg font-bold text-white">
                              {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(subtotal)}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 justify-between items-center">
                            <div className="flex flex-wrap gap-2">
                              <Badge className="bg-slate-700 text-slate-200 border-slate-600 text-xs px-1.5 py-0.5">{new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(subtotal)}</Badge>
                              {discountSum > 0 && (
                                <Badge className="text-orange-300 bg-orange-500/20 border-orange-500/30 text-xs px-1.5 py-0.5">-{new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(Math.round(discountSum * 100) / 100)}</Badge>
                              )}
                            </div>
                            <div className="text-lg font-bold text-white">
                              {new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(totalAfter)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="p-3 border-t border-slate-800">
              <DialogFooter>
                <div className="flex gap-2 justify-end w-full">
                  <Button
                    disabled={addOrderSubmitting}
                    onClick={async () => {
                      if (!ordersEventId || !addOrderTableSlug) return;
                      const items = Object.entries(addOrderQuantities)
                        .filter(([_, q]) => (q || 0) > 0)
                        .map(([name, q]) => {
                          const dish = (addOrderMenu?.dishes || []).find(d => d.name === name);
                          const price = dish && typeof dish.price === 'number' ? dish.price : 0;
                          return { name, quantity: q, price };
                        });
                      if (!items.length) return;
                      setAddOrderSubmitting(true);
                      try {
                        await adminAPI.post(`/orders`, {
                          tableSlug: addOrderTableSlug,
                          items,
                          comment: (addOrderComment || '').trim() || undefined,
                        });
                        setAddOrderOpen(false);
                        setAddOrderMenu({ dishes: [] });
                        setAddOrderQuantities({});
                        setAddOrderComment('');
                        setAddOrderTableSlug('');
                        setAddOrderSubmitting(false);
                        fetchOrders(ordersEventId);
                      } catch (e) {
                        alert('Erreur lors de la création de la commande');
                        setAddOrderSubmitting(false);
                      }
                    }}
                    className="px-5 h-10 text-base text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    Créer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAddOrderOpen(false);
                    }}
                    className="px-5 h-10 text-base border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                  >
                    Annuler
                  </Button>
                </div>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernOrdersTab;
